import React, { FC, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useLocation, useNavigate, useParams } from 'react-router-dom'
import {
  BackArrow,
  Input,
  Select,
  CheckBox,
  RevealButton,
  Button,
  TopProgress
} from '../../../../components'
import {
  Container,
  TitleBig,
  FormWrapper,
  CreateContainer,
  Info,
  InfoText,
  DarkForm
} from './styles'
import * as yup from 'yup';
import { useFormik } from 'formik';
import Label from '../../../../components/label'
import { flavorService } from '../../../../api'
import { useUi } from '../../../../context/ui.context'
import { InfoContainer, InfoTitle } from '../../../../components/info-block/styles'
import { icons } from '../../../../icons'
import { usePageTitle } from '../../../../context/navigationContext'

const CreateFlavorGroup:FC = () => {
  const navigate = useNavigate()
  const location = useLocation()
  const [t] = useTranslation()
  const {viewAlert} = useUi()
  const {groupId} = useParams()
  usePageTitle(t("PAGE_TITLE_FLAVOR_GROUP_CREATE"))
  const [flavorGroup, setFlavorGroup] = useState<any>()
  const [pageLoading, setPageLoading] = useState<boolean>(true)
  const [loading, setLoading] = useState<boolean>(false)
  const [loadingComplete, setLoadingComplete] = useState<boolean>(false)
  const [isDefault, setIsDefault] = useState<boolean>(false)
  const [selectedByDefault, setSelectedByDefault] = useState<boolean>(false)

  const [initialValues, setInitialValues] = useState<any>({
    name:'',
    description:'',
    priority:1,
  })

  const onSubmit = (values:any) => {
    setLoading(true)
    const data = {
      ...values,
      is_default: isDefault,
      selected_by_default: selectedByDefault
    }

    if(!location.pathname.includes('create') && flavorGroup){
      flavorService.editFlavorGroup({
        ...data,
        id: flavorGroup.id
      })
      .then((res) => {
        setLoadingComplete(true)
        setTimeout(() => {
          navigate('/openstack/flavor-groups')
          resetForm()
          setLoadingComplete(false)
          setLoading(false)
        },1000)
      })
      .catch((err:any) => {
        setLoading(false)
        viewAlert({
          severity:'error',
          message: err?.response?.data?.detail || `${t("ERROR_OCCURRED")}`
        })
      })
    } else {
      flavorService.createFlavorGroup(data)
      .then((res) => {
        setLoadingComplete(true)
        setTimeout(() => {
          navigate('/openstack/flavor-groups')
          resetForm()
          setLoadingComplete(false)
          setLoading(false)
        },1000)
      })
      .catch((err:any) => {
        setLoading(false)
        viewAlert({
          severity:'error',
          message: err?.response?.data?.detail || `${t("ERROR_OCCURRED")}`
        })
      })
    }
  }

  const loadFlavorGroup = async () => {
    try{
      if(groupId){
        const groupRes = await flavorService.getFlavorGroup(groupId)
        setFlavorGroup(groupRes?.data)
        setInitialValues({
          name:groupRes?.data?.name,
          description:groupRes?.data?.description,
          priority:groupRes?.data?.priority,
        })
        setIsDefault(groupRes?.data?.is_default)
        setSelectedByDefault(groupRes?.data?.selected_by_default)
      }
      await setPageLoading(false)
    } catch (e) {
      console.error('Error loading flavor group:', e)
      setPageLoading(false)
    }
  }

  useEffect(() => {
    loadFlavorGroup()
  },[])

  const regName = /^[a-zA-Z0-9\\!\"#$%&'()*+,\-./:;<=>?@\[\\\]\^_`{\|}~ ]+$/

  const {
    values,
    errors,
    touched,
    resetForm,
    handleSubmit,
    handleChange,
    setFieldValue,
    handleBlur,
  } = useFormik({
    enableReinitialize: true,
    validationSchema: yup.object().shape({
      name: yup.string()
      .matches(regName,`${t("ONLY_ENGLISH_LETTERS")}`)
      .max(60, `${t("STRING_VALIDATE_MAX")} 60 ${t("CHARACTERS")}`)
      .required(`${t('REQUIRED_FIELD')}`),
      description: yup.string().max(255, `${t("STRING_VALIDATE_MAX")} 255 ${t("CHARACTERS")}`),
      priority: yup.number()
      .min(1, `${t("MIN_VALUE")} 1`)
      .required(`${t('REQUIRED_FIELD')}`),
    }),
    initialValues: {
      ...initialValues,
    },
    onSubmit,
  });

  const formikProps = {
    errors,
    values,
    touched,
    handleChange,
    handleBlur,
  };

  const isEdit = !location.pathname.includes('create') && flavorGroup
  if(pageLoading) return <TopProgress loading={pageLoading}/>
  return(
    <CreateContainer>
      <Container>
        <BackArrow fallbackPath="/openstack/flavor-groups"/>
        <TitleBig style={{margin:'0px'}}>{isEdit ? t("EDIT_FLAVOR_GROUP") + ' ' + (flavorGroup?.name || flavorGroup?.id) : t("CREATE_A_NEW_FLAVOR_GROUP")}</TitleBig>
        <FormWrapper>
          <Input
            fieldName='name'
            title={t("NAME")}
            placeholder=''
            customStyles={{width:'100%'}}
            {...formikProps}
          />
        </FormWrapper>
        <FormWrapper>
          <Input
            isTextArea
            fieldName='description'
            title={t("DESCRIPTION")}
            customStyles={{maxWidth:'550px', height:'100px'}}
            placeholder=''
            {...formikProps}
          />
        </FormWrapper>
        <FormWrapper>
          <Input
            fieldName='priority'
            title={t("PRIORITY")}
            placeholder=''
            type="number"
            customStyles={{width:'100%'}}
            {...formikProps}
          />
        </FormWrapper>
        <CheckBox
          value={isDefault}
          onChange={() => setIsDefault(!isDefault)}
          title={t("IS_DEFAULT")}
        />
        <CheckBox
          value={selectedByDefault}
          onChange={() => setSelectedByDefault(!selectedByDefault)}
          title={t("SELECTED_BY_DEFAULT")}
        />
        <div style={{display:'flex', marginTop:'20px'}}>
          <Button
            variant="primary"
            size="display"
            loading={loading}
            loadingComplete={loadingComplete}
            customStyles={{marginRight:"10px"}}
            onClick={handleSubmit}
            title={isEdit ? t("EDIT") : t("CREATE")}
          />
          <Button
            variant="stroke"
            size="display"
            onClick={() => navigate("/openstack/flavor-groups")}
            title={t("CANCEL")}
          />
        </div>
      </Container>
      {isEdit ? null :
        <Container style={{marginTop:'91px'}}>
          <Info>
            <Label reverse title={t("FLAVOR_GROUP_INFO")}/>
            <InfoText>
              {t("FLAVOR_GROUP_INFO_TEXT_1")}
            </InfoText>
            <InfoText style={{marginTop:"10px"}}>
              {t("FLAVOR_GROUP_INFO_TEXT_2")}
            </InfoText>
          </Info>
        </Container>
      }
    </CreateContainer>
  )
}

export default CreateFlavorGroup
